import { useCallback, useEffect, useState } from "react";
import { LinhaConfig, ExemploConfig } from "@/components/configuracoes/LinhaConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { centsToInput, formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import { definirNomeBonificacao } from "@/hooks/useNomeBonificacao";
import type { ConfigBonificacao } from "@/data/bonificacao";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Formulario {
  nome: string;
  diasExperiencia: string;
  proporcional: boolean;
  diasPeriodo: string;
  teto: string;
  comissionadosRecebem: boolean;
  mesesComissao: string;
}

const paraFormulario = (c: ConfigBonificacao): Formulario => ({
  nome: c.nome,
  diasExperiencia: String(c.diasExperiencia),
  proporcional: c.proporcional,
  diasPeriodo: String(c.diasPeriodo),
  teto: c.tetoSalario === null ? "" : centsToInput(c.tetoSalario),
  comissionadosRecebem: c.comissionadosRecebem,
  mesesComissao: String(c.mesesComissao),
});

const inteiro = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : NaN);

/** Regras de cálculo da bonificação de metas — cada empresa tem a sua. */
export const BonificacaoConfigCard = () => {
  const [form, setForm] = useState<Formulario | null>(null);
  const [salvando, setSalvando] = useState(false);

  const load = useCallback(async () => {
    try {
      setForm(paraFormulario(await api.get<ConfigBonificacao>("/bonificacoes/config")));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar as regras");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof Formulario>(chave: K, valor: Formulario[K]) =>
    setForm((f) => (f ? { ...f, [chave]: valor } : f));

  const salvar = async () => {
    if (!form) return;
    const diasExperiencia = inteiro(form.diasExperiencia);
    const diasPeriodo = inteiro(form.diasPeriodo);
    const mesesComissao = inteiro(form.mesesComissao);
    if (form.nome.trim().length < 3) return void toast.error("Informe o nome da bonificação");
    if (Number.isNaN(diasExperiencia)) return void toast.error("Dias de experiência inválidos");
    if (Number.isNaN(diasPeriodo) || diasPeriodo < 1) return void toast.error("Período do proporcional inválido");
    if (Number.isNaN(mesesComissao) || mesesComissao < 1) return void toast.error("Meses de comissão inválidos");
    let teto: number | null = null;
    try {
      teto = form.teto.trim() ? parseReaisInput(form.teto) : null;
    } catch {
      return void toast.error("Teto inválido");
    }

    setSalvando(true);
    try {
      const salva = await api.put<ConfigBonificacao>("/bonificacoes/config", {
        nome: form.nome.trim(),
        diasExperiencia,
        proporcional: form.proporcional,
        diasPeriodo,
        tetoSalario: teto && teto > 0 ? teto : null,
        comissionadosRecebem: form.comissionadosRecebem,
        mesesComissao,
      });
      setForm(paraFormulario(salva));
      definirNomeBonificacao(salva.nome);
      toast.success("Regras salvas. Valem para as próximas bonificações criadas.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  if (!form) {
    return (
      <section className="rounded-xl border bg-card p-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </section>
    );
  }

  const exp = inteiro(form.diasExperiencia) || 0;
  const periodo = inteiro(form.diasPeriodo) || 365;
  let tetoCents: number | null = null;
  try {
    tetoCents = form.teto.trim() ? Math.round(parseReaisInput(form.teto) * 100) : null;
  } catch {
    tetoCents = null;
  }

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-col gap-1.5 border-b border-border-soft px-6 pb-4 pt-5">
        <h3 className="text-[15px] font-semibold">Regras de cálculo</h3>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Só PJ. Cada bonificação criada guarda uma cópia destas regras: mudar aqui não altera bonificações que já
          existem. O complemento de salário nunca entra na conta.
        </p>
      </div>

      <LinhaConfig
        titulo="Nome"
        htmlFor="bon-nome"
        descricao="Como a empresa chama a bonificação. Aparece no menu e na aba do envio de NF (ex.: Múltiplos de Salário)."
      >
        <Input
          id="bon-nome"
          value={form.nome}
          onChange={(e) => set("nome", e.target.value)}
          maxLength={60}
          className="max-w-[360px]"
        />
      </LinhaConfig>

      <LinhaConfig
        titulo="Dias de experiência"
        htmlFor="bon-exp"
        descricao="Contados da admissão, não entram no cálculo. Quem ainda está em experiência na data-base não recebe. 0 = sem experiência."
      >
        <div className="flex items-center gap-2">
          <Input
            id="bon-exp"
            value={form.diasExperiencia}
            onChange={(e) => set("diasExperiencia", e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">dias</span>
        </div>
      </LinhaConfig>

      <LinhaConfig
        titulo="Teto do salário"
        htmlFor="bon-teto"
        descricao="No múltiplo, o salário fixo acima do teto conta como o teto. Vazio = sem teto."
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">R$</span>
          <Input
            id="bon-teto"
            value={form.teto}
            onChange={(e) => set("teto", maskReaisInput(e.target.value))}
            inputMode="numeric"
            placeholder="Sem teto"
            className="w-40"
          />
        </div>
        {tetoCents ? (
          <ExemploConfig>
            Ex.: salário fixo de {formatCents(tetoCents * 1.1)} a 2x = {formatCents(tetoCents * 2)}
          </ExemploConfig>
        ) : null}
      </LinhaConfig>

      <LinhaConfig
        titulo="Proporcional ao tempo de casa"
        htmlFor="bon-prop"
        descricao="Quem tem menos que o período depois da experiência recebe dias trabalhados ÷ período."
      >
        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm">
          <Switch id="bon-prop" checked={form.proporcional} onCheckedChange={(v) => set("proporcional", v)} />
          {form.proporcional ? "Proporcional" : "Integral para todos"}
        </label>
        {form.proporcional && (
          <>
            <div className="flex items-center gap-2 text-sm">
              <Label htmlFor="bon-periodo" className="font-normal text-muted-foreground">
                Período
              </Label>
              <Input
                id="bon-periodo"
                value={form.diasPeriodo}
                onChange={(e) => set("diasPeriodo", e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                className="h-[34px] w-20"
              />
              <span className="text-muted-foreground">dias</span>
            </div>
            <ExemploConfig>
              Ex.: data-base 31/12 e experiência terminando em 30/06 → 184 dias ÷ {periodo} ≈{" "}
              {Math.round((184 / periodo) * 100)}% do valor. Com {periodo} dias ou mais depois dos {exp} de
              experiência, recebe integral.
            </ExemploConfig>
          </>
        )}
      </LinhaConfig>

      <LinhaConfig
        titulo="Comissionados recebem?"
        htmlFor="bon-com"
        descricao="Desligado: quem recebeu comissão na folha nos últimos meses antes da data-base fica de fora."
      >
        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm">
          <Switch
            id="bon-com"
            checked={form.comissionadosRecebem}
            onCheckedChange={(v) => set("comissionadosRecebem", v)}
          />
          {form.comissionadosRecebem ? "Recebem" : "Não recebem"}
        </label>
        {!form.comissionadosRecebem && (
          <div className="flex items-center gap-2 text-sm">
            <Label htmlFor="bon-meses" className="font-normal text-muted-foreground">
              Últimos
            </Label>
            <Input
              id="bon-meses"
              value={form.mesesComissao}
              onChange={(e) => set("mesesComissao", e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              className="h-[34px] w-16"
            />
            <span className="text-muted-foreground">meses</span>
          </div>
        )}
      </LinhaConfig>

      <div className="flex items-center justify-end gap-3 border-t border-border-soft bg-muted-soft/50 px-6 py-4">
        <Button onClick={() => void salvar()} disabled={salvando}>
          {salvando && <Loader2 className="animate-spin" />}
          Salvar regras
        </Button>
      </div>
    </section>
  );
};
